import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Modal, Field, Select, TextInput, Textarea, PrimaryButton, StatusPill, useFetch, Pagination } from "../../components/crm/ui";
import { Gift, Megaphone, Phone, ArrowRightCircle, Search, Filter, X, ChevronDown, ChevronUp, Target } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";

const STATUS_TONE = { pending: "muted", processing: "warning", paid: "success" };
const PAYMENT_STATUSES = ["all", "pending", "processing", "paid"];
const OPP_STATUSES = ["all", "open", "active", "won", "won_verbal", "lost", "on_hold", "dropped"];
const TYPE_LABEL = { sc: "Solar Consultant", customer: "Customer Referral", incentive: "Sales Target Incentive" };
const TYPE_COLOR = { sc: "#1F8A4C", customer: "#F39C12", incentive: "#7C3AED" };
const STATUS_COLOR = { pending: "#94A3B8", processing: "#F39C12", paid: "#1F8A4C" };
const INSTALL_STATUS_LABEL = {
  not_logged: "Not Logged", scheduled: "Ticket Logged · Scheduled", in_progress: "Installation In Progress",
  on_hold: "Installation On Hold", support_required: "Resolution Support Required",
  installation_completed: "Installation Completed", closed: "Installation Closed",
};
const INSTALL_STATUS_TONE = {
  not_logged: "muted", scheduled: "default", in_progress: "warning", on_hold: "warning",
  support_required: "danger", installation_completed: "success", closed: "success",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

function KpiChip({ label, value, tone = "bg-slate-100 text-slate-800" }) {
  return (
    <div className={"px-2.5 py-1.5 flex items-center gap-1.5 " + tone}>
      <div className="font-display text-[16px] font-bold leading-none tabular-nums">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="border border-[#E2E4DC] bg-white p-3">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] mb-2">{title}</div>
      <div style={{ width: "100%", height: 170 }}>{children}</div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">{label}</div>
      {children}
    </div>
  );
}

function TypeBadge({ type }) {
  const cfg = {
    sc: { cls: "bg-emerald-50 text-emerald-800", Icon: Megaphone, label: "SC" },
    customer: { cls: "bg-amber-50 text-amber-800", Icon: Gift, label: "Customer" },
    incentive: { cls: "bg-violet-50 text-violet-800", Icon: Target, label: "Incentive" },
  }[type] || { cls: "bg-slate-50 text-slate-700", Icon: Gift, label: type };
  const { cls, Icon, label } = cfg;
  return (
    <span className={"inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider " + cls}>
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

export default function ReferrerPayouts() {
  const { data: rowsRaw, reload } = useFetch("/referrer-payouts");
  const rows = useMemo(() => rowsRaw || [], [rowsRaw]);

  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fType, setFType] = useState("all");
  const [fPayStatus, setFPayStatus] = useState("all");
  const [fOppStatus, setFOppStatus] = useState("all");
  const [fOppMonth, setFOppMonth] = useState("all");
  const [fPayoutMonth, setFPayoutMonth] = useState("all");
  const [fMinValue, setFMinValue] = useState("");
  const [fMaxValue, setFMaxValue] = useState("");
  const [fRegion, setFRegion] = useState("all");
  const [fInstallStatus, setFInstallStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const oppMonths = useMemo(() => [...new Set(rows.map((r) => r.opp_month).filter(Boolean))].sort().reverse(), [rows]);
  const payoutMonths = useMemo(() => [...new Set(rows.map((r) => r.payout_month).filter(Boolean))].sort().reverse(), [rows]);
  const regions = useMemo(() => [...new Set(rows.flatMap((r) => r.region_states || []))].sort(), [rows]);

  const activeFilters = useMemo(
    () => [fType !== "all", fPayStatus !== "all", fOppStatus !== "all", fOppMonth !== "all", fPayoutMonth !== "all", fMinValue, fMaxValue, fRegion !== "all", fInstallStatus !== "all"].filter(Boolean).length,
    [fType, fPayStatus, fOppStatus, fOppMonth, fPayoutMonth, fMinValue, fMaxValue, fRegion, fInstallStatus]
  );

  function clearFilters() {
    setFType("all"); setFPayStatus("all"); setFOppStatus("all"); setFOppMonth("all"); setFPayoutMonth("all");
    setFMinValue(""); setFMaxValue(""); setFRegion("all"); setFInstallStatus("all"); setQ(""); setPage(1);
  }

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (fType !== "all" && r.referrer_type !== fType) return false;
      if (fPayStatus !== "all" && (r.payment_status || "pending") !== fPayStatus) return false;
      if (fOppStatus !== "all" && (r.status || "active") !== fOppStatus) return false;
      if (fOppMonth !== "all" && r.opp_month !== fOppMonth) return false;
      if (fPayoutMonth !== "all" && r.payout_month !== fPayoutMonth) return false;
      if (fRegion !== "all" && !(r.region_states || []).includes(fRegion)) return false;
      if (fInstallStatus !== "all" && (r.referrer_type === "incentive" || (r.installation_status || "not_logged") !== fInstallStatus)) return false;
      if (fMinValue && Number(r.estimated_value || 0) < Number(fMinValue)) return false;
      if (fMaxValue && Number(r.estimated_value || 0) > Number(fMaxValue)) return false;
      if (query) {
        const hay = `${r.referrer_name || ""} ${r.customer_name || ""} ${r.code || ""} ${r.name || ""} ${r.sales_employee_name || ""} ${(r.region_states || []).join(" ")}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [rows, fType, fPayStatus, fOppStatus, fOppMonth, fPayoutMonth, fRegion, fInstallStatus, fMinValue, fMaxValue, q]);

  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const kpis = useMemo(() => {
    const acc = { pending: 0, processing: 0, paid: 0, totalValue: 0 };
    const referrers = new Set();
    for (const r of filtered) {
      const s = r.payment_status || "pending";
      acc[s] = (acc[s] || 0) + (r.payment_amount || 0);
      acc.totalValue += Number(r.estimated_value || 0);
      if (r.referrer_id) referrers.add(r.referrer_type + ":" + r.referrer_id);
    }
    return { ...acc, count: filtered.length, referrerCount: referrers.size };
  }, [filtered]);

  const analytics = useMemo(() => {
    const byStatus = { pending: 0, processing: 0, paid: 0 };
    const byType = { sc: 0, customer: 0 };
    const byMonth = {};
    const byReferrer = {};
    for (const r of filtered) {
      const s = r.payment_status || "pending";
      byStatus[s] = (byStatus[s] || 0) + (r.payment_amount || 0);
      byType[r.referrer_type] = (byType[r.referrer_type] || 0) + 1;
      if (r.payout_month) byMonth[r.payout_month] = (byMonth[r.payout_month] || 0) + (r.payment_amount || 0);
      if (r.referrer_id && s === "paid") {
        const key = `${r.referrer_type}:${r.referrer_id}`;
        byReferrer[key] = byReferrer[key] || { name: r.referrer_name || "—", type: r.referrer_type, total: 0 };
        byReferrer[key].total += r.payment_amount || 0;
      }
    }
    const statusData = Object.entries(byStatus).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0);
    const typeData = Object.entries(byType).map(([k, value]) => ({ name: TYPE_LABEL[k], key: k, value })).filter((d) => d.value > 0);
    const months = Object.keys(byMonth).sort().slice(-6);
    const trendData = months.map((m) => ({ month: monthLabel(m), amount: Math.round(byMonth[m]) }));
    const leaderboard = Object.values(byReferrer).sort((a, b) => b.total - a.total).slice(0, 6)
      .map((r) => ({ name: r.name.length > 14 ? r.name.slice(0, 13) + "…" : r.name, amount: Math.round(r.total) }));
    return { statusData, typeData, trendData, leaderboard };
  }, [filtered]);

  return (
    <div data-testid="referrer-payouts-page">
      <PageHeader overline="Payments · SC Referrals · Customer Referrals · Sales Target Incentives" title="Referrer & SC Payouts" />

      {/* KPI strip */}
      <div className="flex items-center gap-1 mb-3 flex-wrap">
        <KpiChip label="Records" value={kpis.count} tone="bg-[#1F4E2C] text-white" />
        <KpiChip label="Unique Referrers" value={kpis.referrerCount} />
        <KpiChip label="Total Opp Value" value={inr(kpis.totalValue)} />
        <KpiChip label="Pending" value={inr(kpis.pending)} tone="bg-slate-100 text-slate-700" />
        <KpiChip label="Processing" value={inr(kpis.processing)} tone="bg-amber-50 text-amber-800" />
        <KpiChip label="Paid" value={inr(kpis.paid)} tone="bg-emerald-50 text-emerald-800" />
      </div>

      {/* Executive charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="payouts-analytics">
        <ChartCard title="Payout Status (₹)">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={analytics.statusData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={55} paddingAngle={2}>
                {analytics.statusData.map((d) => <Cell key={d.name} fill={STATUS_COLOR[d.name] || "#94A3B8"} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} formatter={(v) => inr(v)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Referral Channel Split">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={analytics.typeData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={55} paddingAngle={2}>
                {analytics.typeData.map((d) => <Cell key={d.key} fill={TYPE_COLOR[d.key] || "#94A3B8"} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Payout Trend (6mo, paid ₹)">
          <ResponsiveContainer>
            <AreaChart data={analytics.trendData} margin={{ left: -20, right: 8, top: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fontFamily: "monospace" }} />
              <YAxis tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} formatter={(v) => inr(v)} />
              <Area type="monotone" dataKey="amount" stroke="#1F4E2C" fill="#DDE9E0" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Referrers (paid ₹ only)">
          <ResponsiveContainer>
            <BarChart data={analytics.leaderboard} layout="vertical" margin={{ left: 4, right: 8, top: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 9, fontFamily: "monospace" }} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} formatter={(v) => inr(v)} />
              <Bar dataKey="amount" fill="#F39C12" barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Search + filter row */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 flex items-center gap-2 border border-[#DDE5D8] bg-white px-2 py-1.5 max-w-md">
          <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search referrer, SC, target, customer, opportunity, region…"
            data-testid="payouts-search-input"
            className="flex-1 text-[12.5px] font-mono outline-none bg-transparent"
          />
          {q && <button onClick={() => setQ("")}><X className="w-3.5 h-3.5 text-[#94A3B8] hover:text-[#0F1A12]" /></button>}
        </div>
        <button
          onClick={() => setFilterOpen((v) => !v)}
          data-testid="payouts-filter-toggle"
          className={"px-2.5 py-1.5 border font-mono text-[10.5px] uppercase tracking-widest inline-flex items-center gap-1.5 " + (filterOpen ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "bg-white text-[#0F1A12] border-[#DDE5D8] hover:border-[#1F4E2C]")}
        >
          <Filter className="w-3.5 h-3.5" /> Filters{activeFilters > 0 && ` · ${activeFilters}`}
          {filterOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {activeFilters > 0 && (
          <button onClick={clearFilters} data-testid="payouts-filter-clear" className="text-[10.5px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#0F1A12] px-2">Reset</button>
        )}
        <div className="font-mono text-[10.5px] text-[#475569]">
          <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {rows.length}
        </div>
      </div>

      {filterOpen && (
        <div className="mb-3 border border-[#DDE5D8] bg-[#FFFDF8] p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3" data-testid="payouts-filter-panel">
          <FilterField label="Referrer Type">
            <Select value={fType} onChange={(e) => { setFType(e.target.value); setPage(1); }} data-testid="filter-referrer-type">
              <option value="all">— All —</option>
              <option value="sc">Solar Consultant</option>
              <option value="customer">Customer Referral</option>
              <option value="incentive">Sales Target Incentive</option>
            </Select>
          </FilterField>
          <FilterField label="Payment Status">
            <Select value={fPayStatus} onChange={(e) => { setFPayStatus(e.target.value); setPage(1); }} data-testid="filter-payment-status">
              {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "— All —" : s}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Opportunity Status">
            <Select value={fOppStatus} onChange={(e) => { setFOppStatus(e.target.value); setPage(1); }} data-testid="filter-opp-status">
              {OPP_STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "— All —" : s.replace("_", " ")}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Opportunity Month">
            <Select value={fOppMonth} onChange={(e) => { setFOppMonth(e.target.value); setPage(1); }} data-testid="filter-opp-month">
              <option value="all">— All —</option>
              {oppMonths.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Payout Month">
            <Select value={fPayoutMonth} onChange={(e) => { setFPayoutMonth(e.target.value); setPage(1); }} data-testid="filter-payout-month">
              <option value="all">— All —</option>
              {payoutMonths.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Region">
            <Select value={fRegion} onChange={(e) => { setFRegion(e.target.value); setPage(1); }} data-testid="filter-region">
              <option value="all">— All —</option>
              {regions.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Installation Status">
            <Select value={fInstallStatus} onChange={(e) => { setFInstallStatus(e.target.value); setPage(1); }} data-testid="filter-installation-status">
              <option value="all">— All —</option>
              {Object.entries(INSTALL_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Min Value (₹)">
            <input type="number" min={0} value={fMinValue} onChange={(e) => { setFMinValue(e.target.value); setPage(1); }} placeholder="0" data-testid="filter-min-value"
              className="w-full border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono" />
          </FilterField>
          <FilterField label="Max Value (₹)">
            <input type="number" min={0} value={fMaxValue} onChange={(e) => { setFMaxValue(e.target.value); setPage(1); }} placeholder="Any" data-testid="filter-max-value"
              className="w-full border border-[#DDE5D8] px-2 py-1.5 text-[12px] font-mono" />
          </FilterField>
        </div>
      )}

      <div className="border border-[#E2E4DC] bg-white overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="referrer-payouts-table">
          <thead>
            <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] border-b border-[#E2E4DC] bg-[#F4F6F1]">
              <th className="px-3 py-2.5">Referrer</th>
              <th className="px-3 py-2.5">Opportunity / Target</th>
              <th className="px-3 py-2.5">Customer / Region</th>
              <th className="px-3 py-2.5">SPOC</th>
              <th className="px-3 py-2.5 text-right">Opp Value</th>
              <th className="px-3 py-2.5">Opp Month</th>
              <th className="px-3 py-2.5">Installation</th>
              <th className="px-3 py-2.5">Payment Status</th>
              <th className="px-3 py-2.5 text-right">Payout ₹</th>
              <th className="px-3 py-2.5">Notes</th>
              <th className="px-3 py-2.5">UTR</th>
              <th className="px-3 py-2.5">Payout Month</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r) => (
              <tr key={r.id} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer" data-testid={`payout-row-${r.id}`} onClick={() => setEditing(r)}>
                <td className="px-3 py-2">
                  <div className="font-medium flex items-center gap-1.5">{r.referrer_name || "—"}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <TypeBadge type={r.referrer_type} />
                    {r.referrer_phone && <span className="font-mono text-[10px] text-[#94A3B8] flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{r.referrer_phone}</span>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {r.referrer_type === "incentive" ? (
                    <>
                      <div className="font-mono text-[11px] text-[#6D28D9] inline-flex items-center gap-1"><Target className="w-3 h-3" /> {r.name}</div>
                      <div className="text-[10.5px] text-[#94A3B8] mt-0.5">{r.start_date} → {r.end_date}</div>
                    </>
                  ) : (
                    <>
                      <Link to={`/crm/opportunities/${r.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-[11px] text-[#1F4E2C] hover:underline inline-flex items-center gap-1">
                        {r.code || r.id.slice(0, 8)} <ArrowRightCircle className="w-3 h-3" />
                      </Link>
                      <div className="text-[10.5px] text-[#94A3B8] mt-0.5 capitalize">{(r.status || "active").replace("_", " ")}</div>
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-[#4A5568]">
                  {r.referrer_type === "incentive" ? (r.region_states?.length ? r.region_states.join(", ") : "—") : (r.customer_name || "—")}
                </td>
                <td className="px-3 py-2 text-[11px] text-[#4A5568]">{r.sales_employee_name || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-[11px]">
                  {r.referrer_type === "incentive"
                    ? `${r.my_contribution ?? 0}/${r.target_value ?? 0} ${r.metric_label || ""}`
                    : inr(r.estimated_value)}
                </td>
                <td className="px-3 py-2 font-mono text-[10.5px] text-[#475569] whitespace-nowrap">{monthLabel(r.opp_month)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.referrer_type === "incentive" ? (
                    <span className="text-[10.5px] text-[#94A3B8]">—</span>
                  ) : (
                    <StatusPill value={INSTALL_STATUS_LABEL[r.installation_status || "not_logged"]} tone={INSTALL_STATUS_TONE[r.installation_status || "not_logged"]} />
                  )}
                </td>
                <td className="px-3 py-2"><StatusPill value={r.payment_status || "pending"} tone={STATUS_TONE[r.payment_status || "pending"]} /></td>
                <td className="px-3 py-2 text-right font-mono text-[11px] font-semibold text-[#1F4E2C]">{inr(r.payment_amount)}</td>
                <td className="px-3 py-2 text-[10.5px] text-[#4A5568] max-w-[140px] truncate" title={r.payment_notes || ""}>{r.payment_notes || "—"}</td>
                <td className="px-3 py-2 font-mono text-[10.5px] text-[#1F4E2C]">{r.payment_utr || "—"}</td>
                <td className="px-3 py-2 font-mono text-[10.5px] text-[#475569] whitespace-nowrap">{monthLabel(r.payout_month)}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`payout-edit-${r.id}`} className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline">Edit</button>
                </td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-sm text-[#4A5568]">
                {rows.length === 0 ? "No SC or referrer-linked opportunities yet." : "No records match the filters."}
              </td></tr>
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#1F4E2C] bg-[#F4F6F1] font-semibold" data-testid="payouts-summation-row">
                <td colSpan={4} className="px-3 py-2.5 font-mono text-[10.5px] uppercase tracking-widest text-[#0F1A12]">Total ({filtered.length} filtered)</td>
                <td className="px-3 py-2.5 text-right font-mono text-[11.5px] text-[#1F4E2C]" data-testid="payouts-sum-opp-value">{inr(kpis.totalValue)}</td>
                <td colSpan={3}></td>
                <td className="px-3 py-2.5 text-right font-mono text-[11.5px] text-[#1F4E2C]" data-testid="payouts-sum-payout">{inr(kpis.pending + kpis.processing + kpis.paid)}</td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
        <Pagination page={page} pageSize={pageSize} total={filtered.length}
          onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} testid="payouts-pagination" />
      </div>

      <EditPayoutModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
    </div>
  );
}

function EditPayoutModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setForm({
        status: row.payment_status || "pending",
        amount: row.payment_amount || 0,
        notes: row.payment_notes || "",
        utr: row.payment_utr || "",
      });
    } else {
      setForm(null);
    }
  }, [row]);

  if (!row || !form) return null;

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.set("status", form.status);
      params.set("amount", String(Number(form.amount) || 0));
      params.set("notes", form.notes || "");
      let url;
      if (row.referrer_type === "incentive") {
        params.set("utr", form.utr || "");
        url = `/sales-targets/incentives/${row.id}?${params.toString()}`;
      } else {
        const endpoint = row.referrer_type === "sc" ? "sc-payment" : "referrer-payment";
        if (row.referrer_type === "sc") params.set("utr", form.utr || "");
        url = `/opportunities/${row.id}/${endpoint}?${params.toString()}`;
      }
      await api.put(url);
      toast.success("Payout updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={!!row} onClose={onClose} title={`Payout · ${row.referrer_name || "Referrer"}`}>
      <form onSubmit={save} className="space-y-4" data-testid="payout-edit-form">
        <div className="font-mono text-[11px] text-[#475569] flex items-center gap-2">
          <TypeBadge type={row.referrer_type} />
          {row.referrer_type === "incentive" ? (
            <span>
              Target <span className="font-semibold text-[#0F1A12]">{row.name}</span> · {row.start_date} → {row.end_date} · {row.my_contribution ?? 0}/{row.target_value ?? 0} {row.metric_label}
            </span>
          ) : (
            <span>
              {row.customer_name || "Customer"} · opportunity <span className="font-semibold text-[#0F1A12]">{row.code || row.id.slice(0, 8)}</span> · {inr(row.estimated_value)}
            </span>
          )}
        </div>
        {(row.referrer_type === "sc" || row.referrer_type === "incentive") && row.referrer_upi_id && (
          <div className="bg-[#F4F6F1] px-3 py-2 text-[11px] font-mono text-[#1F4E2C] flex items-center justify-between" data-testid="payout-sc-upi">
            <span>SC's UPI ID on file</span><span className="font-semibold">{row.referrer_upi_id}</span>
          </div>
        )}
        <Field label="Payment Status *">
          <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="payout-status">
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="paid">Paid</option>
          </Select>
        </Field>
        <Field label="Payout Amount (₹)">
          <TextInput type="number" min={0} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payout-amount" />
        </Field>
        {(row.referrer_type === "sc" || row.referrer_type === "incentive") && (
          <Field label="UTR Number">
            <TextInput value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} placeholder="e.g. UTR2026091712345" data-testid="payout-utr" />
          </Field>
        )}
        <Field label="Notes">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payout-notes" />
        </Field>
        <div className="flex justify-end gap-3 pt-2 border-t border-[#E2E4DC]">
          <button type="button" onClick={onClose} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
          <PrimaryButton type="submit" disabled={saving} testid="payout-save-btn">{saving ? "Saving…" : "Save changes"}</PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
